import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Bot, ChevronRight } from "lucide-react";
import { Card, SectionHead } from "@/components/brand/Card";
import { Empty, ErrorBlock, StaleNotice } from "@/components/admin/reference-data/shared";
import { useReferenceDataAgents } from "@/hooks/useApi";
import { agentPath } from "@/lib/reference-data-config-types";

/** Um card por agente: o consultor chega às tabelas pelo agente que as usa. */
export function AgentsTab() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data, isLoading, isError, refetch } = useReferenceDataAgents();
  const agents = data?.agents ?? [];

  return (
    <section>
      <SectionHead
        overline={t("BY AGENT")}
        title={t("Agents")}
        sub={t(
          "Reach the reference data the way consultants know it: pick the agent, then the configuration type. Uploads here update every table of the type as one versioned package.",
        )}
      />
      {isLoading ? (
        <div className="h-24 rounded-lg bg-[#f2f4f7]" />
      ) : isError && !data ? (
        <ErrorBlock message={t("Could not load reference data agents.")} onRetry={() => void refetch()} />
      ) : agents.length === 0 ? (
        <Empty text={t("No agents available.")} />
      ) : (
        <>
          {isError && <StaleNotice />}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {agents.map((agent) => {
              const tableCount = agent.config_types.reduce((n, c) => n + c.tables.length, 0);
              return (
                <button
                  key={agent.key}
                  type="button"
                  onClick={() => navigate(agentPath(agent.key))}
                  className="text-left"
                >
                  <Card className="p-5 hover:border-[#ffc83d] transition-colors">
                    <div className="flex items-start gap-3">
                      <Bot className="w-5 h-5 text-[#b57a00] mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <div className="font-brand font-bold text-[16px] text-[#08254c]">
                          {t(agent.display_name_key)}
                        </div>
                        <p className="text-[12px] text-[#596b85] mt-1">{t(agent.description_key)}</p>
                        <div className="mono muted text-[11px] mt-3">
                          {t("{{types}} configuration type(s) · {{tables}} table(s)", {
                            types: agent.config_types.length,
                            tables: tableCount,
                          })}
                        </div>
                      </div>
                      <ChevronRight className="w-4 h-4 text-[#b2bccb]" />
                    </div>
                  </Card>
                </button>
              );
            })}
          </div>
        </>
      )}
    </section>
  );
}
